// The incoming-call toast (COM-FE-05, COMX-FE-01), mounted once in the authenticated layout: on
// `call.incoming` it shows "{caller} vous appelle" at the top centre with Accept (opens the call
// window, or the call in this tab when the browser blocks windows)
// and Decline (dismisses), and rings (a soft Web Audio tone, see ringtone.ts) while it is on
// screen. It disappears by itself after 30 seconds. It is announced to screen readers as an alert
// and stays on screen without the hover or window-blur pauses of the other toasts, so an
// unanswered call does not linger.
import { useSession } from '@batiflow/client-core/react';
import { isolate } from '@batiflow/ui';
import {
  Button,
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRealtime } from '@/features/realtime';
import { NOTICE_TOASTER_ID } from '@/shared/components/notice-toaster';
import { isCallWindow, openCallWindow } from './call-window';
import { startRingtone } from './ringtone';

/** How long an unanswered call stays on screen. */
export const INCOMING_CALL_TIMEOUT_MS = 30_000;

/** Rings while the toast is on screen: starts when it appears, stops when it goes away. */
function CallRing() {
  useEffect(() => startRingtone(), []);
  return null;
}

export function IncomingCallHost() {
  const { t } = useTranslation('meetings');
  const client = useRealtime();
  const navigate = useNavigate();
  const { dispatchToast, dismissToast } = useToastController(NOTICE_TOASTER_ID);
  const userId = useSession((state) => state.me?.user.id ?? null);

  useEffect(() => {
    // The main window rings; a call window does not ring a second time.
    if (!client || isCallWindow()) return;
    return client.on('call.incoming', ({ data }) => {
      if (data.callerId === userId) return;
      const toastId = `call-${data.meetingId}`;
      const close = () => {
        dismissToast(toastId);
      };
      dispatchToast(
        <Toast>
          <ToastTitle>{t('incoming.title', { name: isolate(data.callerName) })}</ToastTitle>
          <ToastBody>
            {t('incoming.body')}
            <CallRing />
          </ToastBody>
          <ToastFooter>
            <Button
              appearance="primary"
              size="small"
              onClick={() => {
                close();
                if (!openCallWindow(data.meetingId)) {
                  void navigate({ to: '/calls/$meetingId', params: { meetingId: data.meetingId } });
                }
              }}
            >
              {t('incoming.accept')}
            </Button>
            <Button appearance="secondary" size="small" onClick={close}>
              {t('incoming.decline')}
            </Button>
          </ToastFooter>
        </Toast>,
        {
          toastId,
          intent: 'info',
          timeout: INCOMING_CALL_TIMEOUT_MS,
          politeness: 'assertive',
          pauseOnHover: false,
          pauseOnWindowBlur: false,
        },
      );
    });
  }, [client, userId, dispatchToast, dismissToast, navigate, t]);

  return null;
}
